import PT from 'prop-types';
import React, { useEffect, useState } from 'react';

import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Link } from '~/eds/src/components/Link/Link';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { Text } from '~/eds/src/components/Text/Text';
import { useIntl } from 'react-intl';

import Box from '../Box/Box';
import FlexLayout from '../FlexLayout/FlexLayout';
import NumberInput from '../NumberInput/NumberInput';

const MIN = 1;

const labelBox = {
  maxWidth: '200px',
};

const customErrorStyles = {
  width: 'max-content',
  top: -7,
  right: 0,
  left: 'auto',
  height: '24px',
};

export default function Navigator({
  debug = false,
  label,
  onClick,
  onChange,
  onNext,
  onPrev,
  total,
  tooltipNext,
  tooltipPrev,
  value,
}) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState(value);
  useEffect(() => setInputValue(value), [value]);

  const hasPrev = value > MIN;
  const hasNext = value < total;

  const isOutOfRange = (val) => val < MIN || val > total;

  const handleConfirm = () => {
    if (!isOutOfRange(inputValue)) onChange(inputValue);
  };

  return (
    <FlexLayout alignItems="center" space={2}>
      {
        <IconButton
          disabled={!hasPrev}
          icon="chevron-left"
          tooltip={tooltipPrev}
          text={intl.formatMessage({
            id: 'Navigator.previousButton',
            defaultMessage: 'previous',
            description: 'Previous navigation button text',
          })}
          onClick={onPrev}
        />
      }
      <Box sx={labelBox}>
        <Link onClick={onClick}>
          <TruncateText color="action.link" textTransform="uppercase">
            {label}
          </TruncateText>
        </Link>
      </Box>
      <NumberInput
        ariaDescribedby="documentNavigatorRange"
        ariaLabel="Document navigation"
        value={inputValue}
        onChange={setInputValue}
        onBlur={handleConfirm}
        onEnter={handleConfirm}
        min={MIN}
        max={total}
        width="fitContent"
        disableErrorMessage={false}
        customErrorStyles={customErrorStyles}
      />
      <Text>/</Text>
      <Text>{total}</Text>
      {
        <IconButton
          disabled={!hasNext}
          icon="chevron-right"
          tooltip={tooltipNext}
          text={intl.formatMessage({
            id: 'Navigator.nextButton',
            defaultMessage: 'next',
            description: 'Next navigation button text',
          })}
          onClick={onNext}
        />
      }

      {debug && (
        <Text>
          {intl.formatMessage(
            {
              id: 'Navigator.debugValue',
              defaultMessage: 'value: {value}',
              description:
                'Debug text showing current value. Example: value = "42"',
            },
            { value },
          )}
        </Text>
      )}
    </FlexLayout>
  );
}

Navigator.propTypes = {
  /** Toggles debug mode.  */
  debug: PT.bool,
  label: PT.string,
  onChange: PT.func.isRequired,
  onNext: PT.func.isRequired,
  onPrev: PT.func.isRequired,
  total: PT.number.isRequired,
  value: PT.number.isRequired,
};
